import React, { useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, Award } from "lucide-react";
import ReviewsSection from "../components/home/ReviewsSection";
import BrandsSection from "../components/home/BrandsSection";
import ContactForm from "../components/ContactForm";

import IMG_9925 from "../assets/images/gallery/IMG_9925.webp";
import intro_image from "../assets/images/Intro_image_crop_warm.webp";
import neostrataVideo from "../assets/videos/neostrata_facial_video.mp4";

function Home() {
    const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.7;
      
      videoRef.current.play().catch(error => {
        console.log("Video autoplay blocked:", error);
      });
    }
  }, []);
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${intro_image})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-6 leading-tight">
            Välkommen till <span className="font-semibold">Salong Milepa</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed px-4">
            Välkommen till Milepa – din oas för skönhet och avkoppling! Vi erbjuder skräddarsydda behandlingar i en lugn och välkomnande atmosfär, där vårt dedikerade team finns här för att ge dig en fantastisk upplevelse. Vi ser fram emot att ta hand om dig!
          </p>
          <a href="https://www.bokadirekt.se/places/salong-milepa-13263" target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-3 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
              style={{ backgroundColor: 'var(--booking-green)' }}
            >
              Boka Din Tid
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>


      {/* Motto Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* === ÄNDRAD: Mindre video container === */}
            <div className="flex justify-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full max-w-md">
                <video
                                  ref={videoRef} // ← Lägg till ref
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto object-cover"
                  poster={IMG_9925}
                >
                  <source src={neostrataVideo} type="video/mp4" />
                  Din webbläsare stöder inte videouppspelning.
                </video>

                {/* Subtilt overlay för bättre kontrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-12 h-12" style={{ color: 'var(--secondary)' }} />
              </div>
              <div className="mb-6">
                <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-800">
                  Vi säljer inte behandlingar
                </h2>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Vi säljer resultat
                </h3>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                På Salong Milepa fokuserar vi på verkliga, mätbara resultat. Varje behandling är noggrant utformad för att ge dig synliga förbättringar och långvarig effekt. Vi kombinerar expertis, moderna tekniker och genuina omtanke för att leverera den transformation du förtjänar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section
        className="py-24 px-6"
        style={{ background: 'linear-gradient(135deg, rgba(98, 195, 214, 0.05) 0%, rgba(211, 154, 84, 0.05) 100%)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Sparkles className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--secondary)' }} />
            <h2 className="text-4xl md:text-5xl font-light mb-4 text-gray-800">
              Våra <span className="font-semibold" style={{ color: 'var(--primary)' }}>Specialiteter</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professionella behandlingar för kropp och själ
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Ansiktsbehandlingar",
                description: "Skräddarsydda behandlingar för alla hudtyper med fokus på resultat",
              },
              {
                title: "Avancerad Hudvård",
                description: "Moderna tekniker som Microneedling, RF och Plasma Pen för synliga resultat",
              },
              {
                title: "Massage & Välmående",
                description: "Avkopplande massagebehandlingar som återställer balans",
              }
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-3 text-gray-800">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="/Treatments">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 rounded-full border-2 hover:shadow-lg transition-all duration-300"
                style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
              >
                Se Alla Behandlingar
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <ReviewsSection />
      <BrandsSection />
      <ContactForm />

      {/* Final CTA */}
      <section
        className="py-24 px-6 text-center relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
            Redo att Uppleva <span className="font-semibold">Skillnaden?</span>
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Boka din behandling idag och upptäck varför våra kunder älskar oss
          </p>
          <a href="https://www.bokadirekt.se/places/salong-milepa-13263" target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              className="bg-white text-lg px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
              style={{ color: 'var(--primary)' }}
            >
              Boka Nu
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}

export default React.memo(Home);